// Copyright 2026 Sorama B.V.

import { transient } from "aurelia-framework";

/**
 * Time-dependent exponential smoother.
 *
 * Each new sample is blended with the previous smoothed state using:
 *   α = 1 − e^(−|Δt| / τ)
 *   smoothed = α · new + (1 − α) · previous
 *
 * where τ is the time constant and Δt is the elapsed time between samples
 * (both in the same unit). Because α scales with Δt, the
 * effective smoothing is independent of the sampling rate.
 */
@transient()
export class ExponentialSmoother {
    /** Time constant in the same unit as the timestamps passed to {@link smooth}. */
    public timeConstant = 0;

    private smoothedData: number[] = null;
    private lastTimestamp: number = null;

    /**
     * Returns the smoothed version of `data`.
     * On the first call (or after {@link reset}), returns a copy of `data` unchanged.
     * @param data    Incoming data frame.
     * @param timestamp Timestamp of this frame (same unit as {@link timeConstant}).
     */
    public smooth(data: number[], timestamp: number): number[] {
        if (
            this.timeConstant > 0 &&
            this.smoothedData !== null &&
            this.smoothedData.length === data.length &&
            this.lastTimestamp !== null
        ) {
            const delta = Math.abs(timestamp - this.lastTimestamp);
            const alpha = 1 - Math.exp(-delta / this.timeConstant);
            for (let i = 0; i < data.length; i++) {
                this.smoothedData[i] = alpha * data[i] + (1 - alpha) * this.smoothedData[i];
            }
        } else {
            this.smoothedData = data.slice();
        }

        this.lastTimestamp = timestamp;
        return this.smoothedData;
    }

    /** Clears the internal state so the next call to {@link smooth} starts fresh. */
    public reset(): void {
        this.smoothedData = null;
        this.lastTimestamp = null;
    }
}
